import { useEffect, useState } from 'react';
import {
  Combobox, useListCollection, useFilter, ProgressCircle,
} from '@chakra-ui/react';
import type { SaleFunnelType } from '@customTypes/salesFunnels';

type Props = {
  invalid?: boolean;
  salesFunnel: SaleFunnelType[];
  onSearch: (value: string) => Promise<void>;
  value: SaleFunnelType | null;
  onChange: (saleFunnel: SaleFunnelType | null) => void;
};

const SaleFunnelSelect = ({
  invalid, salesFunnel, onSearch, value, onChange,
}: Props) => {
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();
  const [searching, setSearching] = useState(false);

  const { contains } = useFilter({ sensitivity: 'base' });

  const { collection, filter, reset, set } = useListCollection({
    initialItems: salesFunnel,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.name,
    filter: contains,
  });

  const handleInputValueChange = (inputValue: string) => {
    filter(inputValue);
    setSearching(true);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(async () => {
      await onSearch(inputValue);
      setSearching(false);
    }, 1000));
  };

  useEffect(() => {
    if (salesFunnel) {
      set(salesFunnel);
    }
  }, [salesFunnel, set]);

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value.id] : []}
      onInputValueChange={(e) => handleInputValueChange(e.inputValue)}
      onValueChange={({ items }) => onChange(items[0] || null)}
      invalid={invalid}
      variant="subtle"
      openOnClick
    >
      <Combobox.Label>Funil de venda</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input placeholder="Pesquisar..." />
        <Combobox.IndicatorGroup>
          {searching && (
            <ProgressCircle.Root value={null} size="xs">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          )}
          <Combobox.ClearTrigger />
          <Combobox.Trigger onClick={reset} />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content zIndex="popover">
          <Combobox.Empty>Nenhum item encontrado</Combobox.Empty>
          {collection.items.map((item) => (
            <Combobox.Item item={item} key={item.id}>
              {item.name}
              <Combobox.ItemIndicator />
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  );
};

export default SaleFunnelSelect;
